<template>
  <div class="tl mb">
    <Breadcrumbs main="Трансляция MLBB" title="Эфир MLBB"/>

    <div class="container-fluid tl-container mb-stack">
      <!-- компактное состояние подключения: судья + источник -->
      <section v-if="sourceBattleId" class="mb-panel mb-source">
        <div class="mb-source__main">
          <span class="mb-muted mb-small">Судья</span>
          <b class="mb-mono">{{ judgeId || '—' }}</b>
          <span class="mb-source__sep">|</span>
          <span class="mb-muted mb-small">Источник</span>
          <template v-if="battle">
            <b class="mb-ellipsis mb-source__title" :title="battleTitle(battle)">{{ battleTitle(battle) }}</b>
            <span :class="['mb-badge', 'mb-badge--' + stateVariant(battle.state)]">{{ stateLabel(battle.state) }}</span>
            <span v-if="battle.state === 'play'" class="mb-mono">{{ formatGameTime(battle.game_time) }}</span>
            <span v-if="battle.group_id" class="mb-badge mb-badge--success">матч #{{ battle.group_id }}</span>
          </template>
          <span v-else-if="liveLoadError" class="mb-error-text">не удалось загрузить</span>
          <span v-else class="mb-loading">Загружаем</span>
          <span class="mb-spacer"></span>
          <button type="button" class="mb-btn mb-btn--ghost mb-btn--sm" @click="setTab('matches')">Сменить матч</button>
        </div>

        <div v-if="series.length > 1 || newerGame" class="mb-source__series">
          <span class="mb-muted mb-small">Игры серии:</span>
          <button v-for="(g, i) in series"
                  :key="g.id"
                  type="button"
                  :class="['mb-chip', {'is-active': g.id === sourceBattleId}]"
                  :disabled="g.id === sourceBattleId"
                  @click="selectSource(g)">
            Игра {{ i + 1 }} · {{ stateLabel(g.state) }}
          </button>
          <span v-if="newerGame" class="mb-alert">
            Началась следующая игра серии
            <button type="button" class="mb-btn mb-btn--sm" @click="selectSource(newerGame)">Сделать источником</button>
          </span>
        </div>

        <div v-if="battle && !battle.group_id" class="mb-source__unlinked mb-alert">
          Игра не привязана к матчу BIGPLAY — показываем игровые названия команд из Moonton.
          <button type="button" class="mb-btn mb-btn--warn mb-btn--sm" @click="openLink(battle)">Привязать к матчу</button>
        </div>
      </section>

      <MbMoontonStatus v-if="sourceBattleId"
                       :battle="battle"
                       :ws-status="wsStatus"
                       :load-error="liveLoadError"
                       :busy="reconnecting"
                       :now="now"
                       @reconnect="reconnect"/>

      <section class="mb-panel">
        <nav class="mb-tabs" role="tablist">
          <button v-for="t in tabs"
                  :key="t.key"
                  type="button"
                  role="tab"
                  :aria-selected="tab === t.key"
                  :class="['mb-tab', {'is-active': tab === t.key}]"
                  @click="setTab(t.key)">{{ t.title }}</button>
        </nav>

        <div v-if="tab === 'prep'" class="mb-panel__body">
          <div v-if="!sourceBattleId" class="mb-alert mb-alert--info mb-prep-hint">
            Матч не выбран — доступны только «Без графики» и «Заставка». Выберите игру во вкладке «Матчи по ID».
          </div>
          <MbAirPanel :battle="battle"
                      :events="events"
                      :series="series"
                      :post-battles="postBattles"
                      :preview="air.preview"
                      :program="air.program"
                      :now="now"
                      active
                      @preview="patchPreview"
                      @take="take"/>
        </div>

        <div v-if="tab === 'data'" class="mb-panel__body">
          <MbDataTab :battle="battle" :events="events" :timeline="timeline" :series="series" :now="now"/>
        </div>

        <div v-show="tab === 'matches'" class="mb-panel__body">
          <MbMatchesByJudge :judge-id="judgeId"
                            :source-battle-id="sourceBattleId"
                            :judges="judges"
                            :now="now"
                            :reload-key="matchesReloadKey"
                            @search="setJudge"
                            @select="selectSource"
                            @link="openLink"
                            @changed="onBattleChanged"
                            @judges-changed="loadJudges"/>
        </div>
      </section>
    </div>

    <MbLinkMatchModal v-model="linkVisible" :battle="linkBattle" @linked="onLinked"/>
  </div>
</template>

<script>
import api from '@/api/api'
import MbAirPanel from '@/components/MlbbBroadcast/MbAirPanel.vue'
import MbDataTab from '@/components/MlbbBroadcast/MbDataTab.vue'
import MbLinkMatchModal from '@/components/MlbbBroadcast/MbLinkMatchModal.vue'
import MbMatchesByJudge from '@/components/MlbbBroadcast/MbMatchesByJudge.vue'
import MbMoontonStatus from '@/components/MlbbBroadcast/MbMoontonStatus.vue'
import {createBattleLive} from '@/services/mlbbBattleLive'
import {loadHeroCatalog} from '@/services/mlbbHeroCatalog'
import {reconnectWs} from '@/services/websocket'
import {sceneTitle} from '@/common/mlbbOverlay'
import {
  battleTitle,
  formatGameTime,
  parseJudgeId,
  pushRecentJudge,
  stateLabel,
  stateVariant,
} from '@/common/mlbbBroadcast'

const TABS = [
  {key: 'prep', title: 'Подготовка графики'},
  {key: 'data', title: 'Данные MLBB'},
  {key: 'matches', title: 'Матчи по ID'},
]
// «сек назад» и «данные задерживаются» пересчитываем по таймеру, а не по приходу данных
const CLOCK_MS = 5000
// следующую игру серии бэк отдельным событием не присылает — перечитываем список игр матча
const SERIES_POLL_MS = 20000

export default {
  name: 'MlbbBroadcastPage',
  components: {MbAirPanel, MbDataTab, MbLinkMatchModal, MbMatchesByJudge, MbMoontonStatus},
  data() {
    return {
      tabs: TABS,
      tab: 'matches',
      judgeId: null,
      judges: [],
      sourceBattleId: null,
      battle: null,
      events: [],
      timeline: [],
      series: [],
      wsStatus: 'closed',
      liveLoadError: '',
      reconnecting: false,
      linkVisible: false,
      linkBattle: null,
      matchesReloadKey: 0,
      now: Date.now(),
      live: null,
      clock: null,
      seriesTimer: null,
      // выбор режиссёра живёт только в этой вкладке: OBS ставит сцену сам по состоянию игры
      air: {
        preview: {scene: 'none', postId: null},
        program: {scene: 'none', postId: null},
      },
      // завершённые игры серии для «Итогов», догруженные по REST
      postBattleCache: {},
    }
  },
  computed: {
    postBattles() {
      const map = {...this.postBattleCache}
      if (this.battle) map[this.battle.id] = this.battle
      return map
    },
    // игра серии, которая началась после текущего источника
    newerGame() {
      const idx = this.series.findIndex((g) => g.id === this.sourceBattleId)
      if (idx < 0) return null
      return this.series.slice(idx + 1).reverse().find((g) => g.state !== 'end') || null
    },
  },
  watch: {
    'air.preview.postId': 'loadPostBattle',
    'air.program.postId': 'loadPostBattle',
    'battle.group_id'(groupId, prev) {
      if (groupId !== prev) this.loadSeries()
    },
    'battle.state'(state, prev) {
      // игра закончилась — следующая появится в списке серии
      if (state === 'end' && prev && prev !== 'end') this.loadSeries()
    },
  },
  created() {
    const q = this.$route.query
    this.judgeId = parseJudgeId(q.judge)
    const battleId = parseJudgeId(q.battle)
    this.tab = TABS.some((t) => t.key === q.tab) ? q.tab : (battleId ? 'data' : 'matches')
    if (battleId) this.startLive(battleId)
    this.loadJudges()
    loadHeroCatalog()
  },
  mounted() {
    this.clock = setInterval(() => {
      this.now = Date.now()
    }, CLOCK_MS)
    this.seriesTimer = setInterval(() => {
      if (document.visibilityState === 'visible') this.loadSeries()
    }, SERIES_POLL_MS)
  },
  beforeDestroy() {
    clearInterval(this.clock)
    clearInterval(this.seriesTimer)
    this.stopLive()
  },
  methods: {
    battleTitle,
    formatGameTime,
    stateLabel,
    stateVariant,

    syncQuery() {
      const query = {...this.$route.query}
      const set = (key, value) => {
        if (value) query[key] = String(value)
        else delete query[key]
      }
      set('judge', this.judgeId)
      set('battle', this.sourceBattleId)
      set('tab', this.tab)
      const same = ['judge', 'battle', 'tab'].every((k) => (this.$route.query[k] || undefined) === query[k])
      if (!same) this.$router.replace({query}).catch(() => {})
    },

    setTab(key) {
      this.tab = key
      this.syncQuery()
    },

    setJudge(id) {
      this.judgeId = id
      pushRecentJudge(id)
      this.syncQuery()
    },

    async loadJudges() {
      try {
        const res = await api.mlbbEsports.judges({skipErrorToast: true})
        this.judges = res?.data?.data || []
      } catch (e) {
        this.judges = []
      }
    },

    /* ---------- источник данных ---------- */

    async selectSource(b) {
      if (!b || b.id === this.sourceBattleId) return
      if (this.air.program.scene !== 'none' && !(await this.confirmSourceChange(b))) return
      if (this.judgeId) pushRecentJudge(this.judgeId)
      // список уже отдал полный объект — показываем сразу, live догрузит остальное
      this.startLive(b.id, b)
      this.tab = 'data'
      this.syncQuery()
    },

    startLive(battleId, initial = null) {
      this.stopLive()
      // завершённая игра могла остаться в «Итогах» — сохраняем её, раз live-источник меняется
      if (this.battle?.state === 'end') this.$set(this.postBattleCache, this.battle.id, this.battle)
      this.sourceBattleId = battleId
      this.battle = initial && initial.id === battleId ? initial : null
      this.events = []
      this.timeline = []
      this.series = []
      this.liveLoadError = ''
      this.live = createBattleLive({
        battleId,
        onBattle: (b) => {
          this.battle = b
          this.liveLoadError = ''
        },
        onEvents: (all) => {
          this.events = all
        },
        onTimeline: (points) => {
          this.timeline = points
        },
        onStatus: (status) => {
          this.wsStatus = status
        },
        onError: (e) => {
          this.liveLoadError = e?.response?.status === 404
              ? 'Игра не найдена'
              : (e?.response?.data?.message || e?.message || 'Ошибка загрузки')
        },
      })
      this.live.start().then(() => this.loadSeries())
    },

    confirmSourceChange(b) {
      return this.$bvModal.msgBoxConfirm(
          `Сейчас в эфире «${sceneTitle(this.air.program.scene)}». Графика сразу переключится на данные игры «${battleTitle(b)}».`,
          {
            title: 'Сменить источник во время эфира?',
            okTitle: 'Сменить источник',
            cancelTitle: 'Отмена',
            okVariant: 'danger',
            centered: true,
          },
      ).catch(() => false)
    },

    stopLive() {
      if (this.live) this.live.stop()
      this.live = null
    },

    async loadSeries() {
      const groupId = this.battle?.group_id
      if (!groupId) {
        this.series = []
        return
      }
      try {
        const res = await api.mlbbEsports.groupBattles(groupId, {skipErrorToast: true})
        if (groupId !== this.battle?.group_id) return
        this.series = res?.data?.data || []
      } catch (e) {
        // серия — подсказка, без неё раздел работает
      }
    },

    /* ---------- эфир ---------- */

    patchPreview(patch) {
      this.air.preview = {...this.air.preview, ...patch}
    },

    take() {
      this.air.program = {...this.air.preview}
    },

    async loadPostBattle(id) {
      if (!id || id === this.battle?.id || this.postBattleCache[id]) return
      try {
        const res = await api.mlbbEsports.battle(id, {skipErrorToast: true})
        const b = res?.data?.data
        if (b) this.$set(this.postBattleCache, id, b)
      } catch (e) {
        // итоги покажут заглушку «выберите игру»
      }
    },

    /* ---------- действия ---------- */

    async reconnect() {
      if (!this.battle) return
      this.reconnecting = true
      try {
        // опрос Moonton остановлен или упал — просим бэк включить его и дёрнуть один раз
        if (this.battle.poll_enabled === false || this.battle.last_sync_error) {
          await api.mlbbEsports.resyncBattle(this.battle.id)
          this.matchesReloadKey += 1
        }
        reconnectWs()
        await this.live.refresh()
      } catch (e) {
        // тост покажет интерсептор
      } finally {
        this.reconnecting = false
      }
    },

    openLink(b) {
      this.linkBattle = b
      this.linkVisible = true
    },

    onLinked() {
      this.matchesReloadKey += 1
      this.onBattleChanged(this.linkBattle?.id)
    },

    onBattleChanged(battleId) {
      // после link/unlink/resync бэк пришлёт updated по WS, но служебные поля опроса — только по REST
      if (battleId && battleId === this.sourceBattleId && this.live) this.live.refresh()
    },
  },
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/admin-list.scss";
</style>

<style lang="scss">
@import "~@/assets/scss/mlbb-broadcast.scss";

.mb-source {
  display: flex;
  flex-direction: column;

  &__main, &__series, &__unlinked {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 12px 18px;
    font-size: 13px;
  }

  &__series, &__unlinked { border-top: 1px solid var(--tl-border); }
  &__unlinked { border-radius: 0 0 var(--tl-radius) var(--tl-radius); }
  &__title { max-width: 440px; }
  &__sep { color: var(--tl-border); }

  .mb-chip:disabled { cursor: default; }
}

.mb-prep-hint { margin-bottom: 14px; }
</style>
